import dayjs from 'dayjs';
import { Box, ButtonGroup, Flex, Heading, HStack, IconButton, Input, List, Text, VStack } from '@chakra-ui/react';
import { daysOffUpdate } from '../../../operations/admin/daysOffUpdate';
import { FiMinus, FiPlus } from 'react-icons/fi';
import { formatDate, formatMonth } from '@opanuj/shared/utils';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEffect, useState } from 'react';

type DaysOffFormProps = {
	daysOff?: string[];
};

export const DaysOffForm = ({ daysOff = [] }: DaysOffFormProps) => {
	const database = useDatabase();
	const [dayOffInputValue, setDayOffInputValue] = useState<string>('');

	useEffect(() => {
		setDayOffInputValue('');
	}, [daysOff]);

	const isDayOffInputValueValid = Boolean(dayOffInputValue) && !daysOff.includes(dayOffInputValue);

	const handleDayOffAdd = () => {
		if (!isDayOffInputValueValid) return;

		const newDaysOff = [...daysOff, dayOffInputValue].sort();
		daysOffUpdate(database, newDaysOff);
	};

	const handleDayOffRemove = (dayOff: string) => {
		if (!daysOff.includes(dayOff)) return;

		const newDaysOff = daysOff.filter((x) => x !== dayOff);
		daysOffUpdate(database, newDaysOff);
	};

	const daysOffByMonth = daysOff.reduce((acc, dayOff) => {
		const monthKey = dayjs(dayOff).format('YYYY-MM');
		if (!acc[monthKey]) {
			acc[monthKey] = [];
		}
		acc[monthKey].push(dayOff);
		return acc;
	}, {} as Record<string, string[]>);

	return (
		<Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={{ base: 4, md: 5 }} bg="white">
			<VStack alignItems="stretch" gap={3}>
				<Heading as="span" size="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider">
					Dni wolne od zajęć
				</Heading>
				<ButtonGroup alignItems="center" w="100%" mb={1}>
					<Text fontSize="sm" fontWeight="medium" mr={2} whiteSpace="nowrap">
						Dodaj dzień wolny:
					</Text>
					<Input
						value={dayOffInputValue}
						variant="subtle"
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDayOffInputValue(e.target.value)}
						name="Dzień wolny"
						type="date"
						maxW="160px"
					/>
					<IconButton
						aria-label="Dodaj"
						disabled={!isDayOffInputValueValid}
						onClick={handleDayOffAdd}
					>
						<FiPlus />
					</IconButton>
				</ButtonGroup>
				<List.Root as="ul" listStyleType="circle" ml={4} gap={4}>
					{Object.entries(daysOffByMonth).map(([monthKey, days]) => (
						<List.Item as="li" key={monthKey}>
							<Heading size="sm" mb={2}>
								{formatMonth(dayjs(monthKey).month())} {dayjs(monthKey).year()}
							</Heading>
							<HStack gap={2} wrap="wrap" ml={1}>
								{days.map((dayOff) => (
									<Flex key={dayOff} alignItems="center" justifyContent="space-between" borderWidth="1px" borderColor="gray.200" bg="white" borderRadius="md" p={1} maxW="160px">
										<Text fontSize="sm" fontWeight="medium" px={2} py={1}>
											{formatDate(dayOff)}
										</Text>
										<IconButton aria-label="Usuń" size="xs" variant="ghost" onClick={() => handleDayOffRemove(dayOff)}>
											<FiMinus />
										</IconButton>
									</Flex>
								))}
							</HStack>
						</List.Item>
					))}
				</List.Root>
			</VStack>
		</Box>
	);
};
